import { FC } from 'react'
import { useAtomValue } from 'jotai'
import { inflect } from 'inflection'

import {
  Text,
  GreyGrey70,
  GreyGrey80,
  IndigoIndigo50,
  Primary13Regular,
  Primary13SemiBold,
  XYChart,
  XYChartTooltipTemplate,
  Flex
} from '@sequencehq/core-components'
import { format, parseDateFromApi } from '@sequencehq/utils/dates'
import { timescaleAtom } from './atoms'
import { getChartData, getUnitTickValues, timescaleToDays } from './utils'
import { viewsByDayAtom } from '../../../atoms'

export const TotalViewsGraph: FC = () => {
  const timescale = useAtomValue(timescaleAtom)
  const viewsByDayData = useAtomValue(viewsByDayAtom)

  const days = timescaleToDays(timescale)
  const unitTickValues = getUnitTickValues({ data: viewsByDayData })
  const chartData = getChartData({ data: viewsByDayData, days })

  return (
    <Flex
      width="100%"
      height="115px"
      data-testid="quoteAnalytics.chart.container"
    >
      <XYChart
        variant="line"
        dateFormat="d MMM"
        dateFrom={chartData.at(0)?.date ?? ''}
        dateTo={chartData.at(-1)?.date ?? ''}
        data={chartData}
        unitTickValues={unitTickValues}
        unit=""
        useLinearCurve
        renderTooltip={props => <ViewChartTooltip {...props} />}
        data-testid="quoteAnalytics.chart"
        customXAxisDateFormatter={date =>
          format(parseDateFromApi(date) ?? '', 'd MMM')
        }
      />
    </Flex>
  )
}

const ViewChartTooltip: FC<{ date: string; value: number }> = ({
  date,
  value
}) => {
  const DATE_FORMAT: string = 'EEE MMM d, yyyy'

  return (
    <XYChartTooltipTemplate data-testid="quoteAnalytics.chart.tooltip">
      <Text
        {...Primary13SemiBold}
        color={GreyGrey70}
        data-testid="quoteAnalytics.chart.tooltip.header"
      >
        {format(parseDateFromApi(date) ?? '', DATE_FORMAT)}
      </Text>
      <>
        <Text
          as={Flex}
          {...Primary13Regular}
          color={GreyGrey70}
          alignItems="center"
          gap={2}
          data-testid="quoteAnalytics.chart.tooltip.label"
        >
          <Flex h={2} w={2} bg={IndigoIndigo50} borderRadius="full" />
          {format(parseDateFromApi(date) ?? '', DATE_FORMAT)}
        </Text>
        <Text
          {...Primary13SemiBold}
          color={GreyGrey80}
          data-testid="quoteAnalytics.chart.tooltip.value"
        >
          {value.toLocaleString('en-US')} {inflect('view', value)}
        </Text>
      </>
    </XYChartTooltipTemplate>
  )
}
